/**
 * Shifts Information Component
 *
 * @module @features/departments/components/ShiftsInfo
 */

import React, { useState, useCallback } from 'react';
import { Tabs, Tab, TabPanel } from '@dynamon-as/ui';
import { useI18n } from '@shared/i18n';
import type { Employee, RestEmployeeShiftType } from '@shared/types';
import { DaySegment } from '@shared/types/enums';
import { NameAndValue } from './NameAndValue';
import styles from '../MyDepartments.module.css';

/**
 * Props for ShiftsInfo component
 */
export interface ShiftsInfoProps {
    employee: Employee | undefined;
}

/**
 * Displays employee shifts information with tabs
 */
export function ShiftsInfo({ employee }: ShiftsInfoProps): React.ReactElement {
    const { t } = useI18n();
    const [selectedDaySegment, setSelectedDaySegment] = useState<DaySegment>(
        DaySegment.D,
    );

    const handleDaySegmentChange = useCallback((value: string | null): void => {
        if (
            value !== null &&
            Object.values(DaySegment).includes(value as DaySegment)
        ) {
            setSelectedDaySegment(value as DaySegment);
        }
    }, []);

    // Setting names for each shift type
    const dayShiftSettingNames: Record<string, string> = {
        dayShiftMinSequence: t('departments.shifts.dayShifts.minSequence'),
        dayShiftMaxSequence: t('departments.shifts.dayShifts.maxSequence'),
        dayShiftsMaxPerWeek: t('departments.shifts.dayShifts.maxPerWeek'),
        maxDayShifts: t('departments.shifts.dayShifts.maxInTurnus'),
    };

    const eveningShiftSettingNames: Record<string, string> = {
        eveningShiftMinSequence: t(
            'departments.shifts.eveningShifts.minSequence',
        ),
        eveningShiftMaxSequence: t(
            'departments.shifts.eveningShifts.maxSequence',
        ),
        eveningShiftsMaxPerWeek: t(
            'departments.shifts.eveningShifts.maxPerWeek',
        ),
        maxEveningShifts: t('departments.shifts.eveningShifts.maxInTurnus'),
    };

    const nightShiftSettingNames: Record<string, string> = {
        nightShiftMinSequence: t('departments.shifts.nightShifts.minSequence'),
        nightShiftMaxSequence: t('departments.shifts.nightShifts.maxSequence'),
        nightShiftsMaxPerWeek: t('departments.shifts.nightShifts.maxPerWeek'),
        maxNightShifts: t('departments.shifts.nightShifts.maxInTurnus'),
    };

    const longShiftSettingNames: Record<string, string> = {
        longShiftMinSequence: t('departments.shifts.longShifts.minSequence'),
        longShiftMaxSequence: t('departments.shifts.longShifts.maxSequence'),
        longShiftsMaxPerWeek: t('departments.shifts.longShifts.maxPerWeek'),
        maxLongShifts: t('departments.shifts.longShifts.maxInTurnus'),
    };

    const getShiftSettings = (
        settings: Record<string, string>,
    ): React.ReactElement => {
        const employeeRecord = employee as unknown as Record<string, unknown>;
        return (
            <div className={styles.shiftSettings}>
                {Object.keys(settings).map((el): React.ReactElement => {
                    const val = employeeRecord[el];
                    const displayValue = val ?? '-';
                    return (
                        <NameAndValue
                            key={el}
                            name={settings[el]}
                            value={displayValue as React.ReactNode}
                        />
                    );
                })}
                <div className={styles.divider} />
                <NameAndValue
                    name={<strong>{t('departments.shifts.shiftCodes')}</strong>}
                    value={
                        <strong>{t('departments.shifts.maxPerWeek')}</strong>
                    }
                />
                {employee?.restShiftTypes
                    ?.filter((sh) => sh.daySegment === selectedDaySegment)
                    ?.map((sh) => {
                        const maxPerWeek =
                            employee?.shiftTypes?.[sh.code]?.maxPerWeek;
                        return (
                            <NameAndValue
                                key={sh.id}
                                name={sh.code}
                                value={maxPerWeek ?? '-'}
                            />
                        );
                    })}
            </div>
        );
    };

    const hasShiftsForSegment = (segment: DaySegment): boolean => {
        const shiftTypes = employee?.shiftTypes ?? {};
        return Object.values(shiftTypes).some(
            (ds: RestEmployeeShiftType) =>
                (ds as { daySegment?: DaySegment }).daySegment === segment,
        );
    };

    const validDaySegments = [
        DaySegment.D,
        DaySegment.A,
        DaySegment.N,
        DaySegment.L,
    ];

    return (
        <>
            <NameAndValue
                name={t('departments.shifts.minSequence')}
                value={
                    (employee?.shiftMinSequence ??
                    employee?.shiftMinSequence === 0)
                        ? '0'
                        : '-'
                }
            />
            <NameAndValue
                name={t('departments.shifts.maxSequence')}
                value={employee?.shiftMaxSequence ?? '-'}
            />
            <NameAndValue
                name={t('departments.shifts.maxSequenceWeekend')}
                value={
                    (employee?.weekendShiftMaxSequence ??
                    employee?.weekendShiftMaxSequence === 0)
                        ? '0'
                        : '-'
                }
            />
            <NameAndValue
                name={t('departments.shifts.maxInTurnus')}
                value={
                    (employee?.maxShifts ?? employee?.maxShifts === 0)
                        ? '0'
                        : '-'
                }
            />

            <div className={styles.shiftTabsContainer}>
                <span className={styles.shiftTabsLabel}>
                    {t('departments.shifts.selectCategory')}
                </span>
                <Tabs
                    value={selectedDaySegment}
                    onChange={(_, value) =>
                        handleDaySegmentChange(value as string)
                    }
                    className={styles.shiftTabs}
                >
                    {validDaySegments.map((segmentValue) => (
                        <Tab
                            key={segmentValue}
                            value={segmentValue}
                            label={t(
                                `departments.daySegments.${segmentValue}` as const,
                            )}
                            data-testid={`tab-${segmentValue}`}
                        />
                    ))}
                </Tabs>
            </div>

            {validDaySegments.map((segmentValue) => {
                let content: React.ReactNode;
                if (hasShiftsForSegment(segmentValue)) {
                    switch (segmentValue) {
                        case DaySegment.D:
                            content = getShiftSettings(dayShiftSettingNames);
                            break;
                        case DaySegment.A:
                            content = getShiftSettings(
                                eveningShiftSettingNames,
                            );
                            break;
                        case DaySegment.N:
                            content = getShiftSettings(nightShiftSettingNames);
                            break;
                        case DaySegment.L:
                            content = getShiftSettings(longShiftSettingNames);
                            break;
                        default:
                            content = (
                                <div className={styles.emptyPlaceholder}>
                                    {t('common.noDataAvailable')}
                                </div>
                            );
                    }
                } else {
                    content = (
                        <div className={styles.emptyPlaceholder}>
                            {t('common.noDataAvailable')}
                        </div>
                    );
                }
                return (
                    <TabPanel
                        key={segmentValue}
                        index={segmentValue}
                        activeIndex={selectedDaySegment}
                    >
                        {content}
                    </TabPanel>
                );
            })}
        </>
    );
}
